/**
 * 发行人工作台 - 统一版本
 * 基于StandardPageLayout重构，确保样式一致性
 * 集成后端API，显示真实的问卷状态和审核反馈
 */
import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Row,
  Col,
  Steps,
  Progress,
  Tag,
  Typography,
  List,
  Timeline,
  Space,
  Avatar,
  message,
  Alert,
  Button,
  Spin,
  Empty,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  BankOutlined,
  TeamOutlined,
  EditOutlined,
  SendOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { StandardPageLayout } from '../../components/layout';
import { useGetIssuerProjectDetailQuery } from '../../store/api/issuerProjectApi';
import { QuestionnaireResubmissionModal } from '../../components/questionnaire/QuestionnaireResubmissionModal';

const { Text, Title } = Typography;

// 发行里程碑数据 - i18n aware
const getBondIssuanceMilestones = (t: any) => [
  {
    title: t('project:issuerWorkbench.milestones.initialQuestionnaire'),
    description: t(
      'project:issuerWorkbench.milestones.initialQuestionnaireDesc'
    ),
    status: 'finish' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.enterpriseInfo'),
      t('project:issuerWorkbench.milestones.subSteps.projectOverview'),
      t('project:issuerWorkbench.milestones.subSteps.greenClassification'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.detailedAssessment'),
    description: t('project:issuerWorkbench.milestones.detailedAssessmentDesc'),
    status: 'finish' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.environmentalBenefits'),
      t('project:issuerWorkbench.milestones.subSteps.financialAnalysis'),
      t('project:issuerWorkbench.milestones.subSteps.riskAssessment'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.issuancePreparation'),
    description: t(
      'project:issuerWorkbench.milestones.issuancePreparationDesc'
    ),
    status: 'process' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.greenCertification'),
      t('project:issuerWorkbench.milestones.subSteps.legalDocuments'),
      t('project:issuerWorkbench.milestones.subSteps.issuancePlan'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.regulatoryFiling'),
    description: t('project:issuerWorkbench.milestones.regulatoryFilingDesc'),
    status: 'wait' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.filingMaterials'),
      t('project:issuerWorkbench.milestones.subSteps.regulatoryReview'),
      t('project:issuerWorkbench.milestones.subSteps.approvalDocuments'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.ratingApplication'),
    description: t('project:issuerWorkbench.milestones.ratingApplicationDesc'),
    status: 'wait' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.ratingAgency'),
      t('project:issuerWorkbench.milestones.subSteps.ratingReport'),
      t('project:issuerWorkbench.milestones.subSteps.ratingConfirmation'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.issuanceListing'),
    description: t('project:issuerWorkbench.milestones.issuanceListingDesc'),
    status: 'wait' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.roadshow'),
      t('project:issuerWorkbench.milestones.subSteps.bookBuilding'),
      t('project:issuerWorkbench.milestones.subSteps.issuancePricing'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.fundManagement'),
    description: t('project:issuerWorkbench.milestones.fundManagementDesc'),
    status: 'wait' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.dedicatedAccount'),
      t('project:issuerWorkbench.milestones.subSteps.usageMonitoring'),
      t('project:issuerWorkbench.milestones.subSteps.periodicReporting'),
    ],
  },
  {
    title: t('project:issuerWorkbench.milestones.duration'),
    description: t('project:issuerWorkbench.milestones.durationDesc'),
    status: 'wait' as const,
    subSteps: [
      t('project:issuerWorkbench.milestones.subSteps.informationDisclosure'),
      t('project:issuerWorkbench.milestones.subSteps.interestPayment'),
      t('project:issuerWorkbench.milestones.subSteps.environmentalTracking'),
    ],
  },
];

// 关键任务数据 - i18n aware
const getKeyTasks = (t: any) => [
  {
    id: 1,
    title: t('project:issuerWorkbench.keyTasks.greenCertification'),
    progress: 75,
    deadline: '2024-03-25',
    status: 'normal' as const,
  },
  {
    id: 2,
    title: t('project:issuerWorkbench.keyTasks.legalReview'),
    progress: 45,
    deadline: '2024-03-22',
    status: 'urgent' as const,
  },
  {
    id: 3,
    title: t('project:issuerWorkbench.keyTasks.environmentalReport'),
    progress: 90,
    deadline: '2024-03-28',
    status: 'normal' as const,
  },
  {
    id: 4,
    title: t('project:issuerWorkbench.keyTasks.filingMaterials'),
    progress: 20,
    deadline: '2024-03-30',
    status: 'pending' as const,
  },
];

// 项目团队数据 - i18n aware
const getProjectTeam = (t: any) => [
  {
    name: '李明',
    role: t('project:issuerWorkbench.projectTeam.projectManager'),
    avatar: '李',
    phone: '138****8888',
    email: 'liming@company.com',
  },
  {
    name: '王芳',
    role: t('project:issuerWorkbench.projectTeam.cfo'),
    avatar: '王',
    phone: '139****9999',
    email: 'wangfang@company.com',
  },
  {
    name: '张强',
    role: t('project:issuerWorkbench.projectTeam.legalManager'),
    avatar: '张',
    phone: '137****7777',
    email: 'zhangqiang@company.com',
  },
  {
    name: '陈慧',
    role: t('project:issuerWorkbench.projectTeam.environmentalExpert'),
    avatar: '陈',
    phone: '136****6666',
    email: 'chenhui@company.com',
  },
];

const IssuerWorkbench: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // 重新提交模态框状态
  const [resubmissionModalVisible, setResubmissionModalVisible] =
    useState(false);

  // 使用API查询项目详情
  const {
    data: projectDetailData,
    isLoading,
    error,
    refetch,
  } = useGetIssuerProjectDetailQuery(
    { projectId: projectId || 'proj_001' }, // 默认使用proj_001
    { skip: !projectId }
  );

  const displayProject = projectDetailData?.data?.project;

  // 页面配置
  const title = t('project:issuerWorkbench.title');
  const subtitle = t('project:issuerWorkbench.subtitle');
  const breadcrumbs = [
    { label: t('project:issuerWorkbench.breadcrumb.home'), path: '/' },
    {
      label: t('project:issuerWorkbench.breadcrumb.myQuestionnaires'),
      path: '/issuer/questionnaires',
    },
    {
      label:
        displayProject?.name ||
        t('project:issuerWorkbench.breadcrumb.projectWorkbench'),
    },
  ];

  // 统计数据
  const statistics = [
    {
      value: displayProject ? `${displayProject.progress}%` : '45%',
      label: t('project:issuerWorkbench.stats.progress'),
      color: '#fff',
    },
    {
      value: '3/8',
      label: t('project:issuerWorkbench.stats.completedStages'),
      color: '#fff',
    },
    {
      value: `5${t('project:issuerWorkbench.stats.days')}`,
      label: t('project:issuerWorkbench.stats.remainingTime'),
      color: '#fff',
    },
  ];

  // 快捷操作 - Bug #20: 暂时隐藏未实现的功能
  const actions: any[] = [
    // {
    //   label: '编辑项目',
    //   icon: <EditOutlined />,
    //   onClick: () => message.success('编辑项目功能开发中'),
    // },
    // {
    //   label: '发起沟通',
    //   icon: <SendOutlined />,
    //   onClick: () => message.success('沟通功能开发中'),
    //   style: {
    //     background: 'rgba(255, 255, 255, 0.25)',
    //     borderColor: 'rgba(255, 255, 255, 0.4)',
    //     boxShadow: '0 2px 4px rgba(255, 255, 255, 0.1)',
    //   },
    // },
  ];

  // 统一的Banner内容 - 优化布局，减少拥挤感
  const customBannerContent = displayProject ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: '32px',
      }}
    >
      {/* 左侧：项目基本信息 - 更宽松的布局 */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}
      >
        {/* 项目信息 - 更清晰的层次 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '19px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '6px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
              lineHeight: '1.3',
            }}
          >
            {displayProject.name}
          </div>
          <div
            style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.85)',
              marginBottom: '10px',
              fontWeight: '500',
            }}
          >
            {t('project:issuerWorkbench.issuer')}：{displayProject.issuer_name}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {displayProject.tags?.map((tag, index) => (
              <span
                key={index}
                style={{
                  padding: '4px 8px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '5px',
                  fontSize: '13px',
                  color: '#fff',
                  fontWeight: '500',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* 右侧：统计数据 - 使用统一的卡片样式，增加间距 */}
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
        <div
          style={{
            padding: '16px 20px',
            background: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '100px',
          }}
        >
          <Text
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#fff',
              display: 'block',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.2',
            }}
          >
            {(displayProject.issuance_amount / 100000000).toFixed(1)}
            {t('project:issuerWorkbench.stats.billion')}
          </Text>
          <Text
            style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: '500',
              marginTop: '4px',
              display: 'block',
            }}
          >
            {t('project:issuerWorkbench.stats.issuanceScale')}
          </Text>
        </div>

        <div
          style={{
            padding: '16px 20px',
            background: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '100px',
          }}
        >
          <Text
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#fff',
              display: 'block',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.2',
            }}
          >
            {displayProject.interest_rate || 4.5}%
          </Text>
          <Text
            style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: '500',
              marginTop: '4px',
              display: 'block',
            }}
          >
            {t('project:issuerWorkbench.stats.expectedRate')}
          </Text>
        </div>

        <div
          style={{
            padding: '16px 20px',
            background: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '100px',
          }}
        >
          <Text
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#fff',
              display: 'block',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.2',
            }}
          >
            {displayProject.progress}%
          </Text>
          <Text
            style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: '500',
              marginTop: '4px',
              display: 'block',
            }}
          >
            {t('project:issuerWorkbench.stats.progressComplete')}
          </Text>
        </div>
      </div>
    </div>
  ) : null;

  // 渲染问卷状态卡片
  const renderQuestionnaireStatus = () => {
    if (
      !displayProject?.questionnaire_status &&
      !displayProject?.review_status
    ) {
      return null;
    }

    const status =
      displayProject.review_status || displayProject.questionnaire_status;

    // 状态颜色映射
    const getStatusColor = (statusValue: string) => {
      switch (statusValue) {
        case 'approved':
          return 'success';
        case 'rejected':
          return 'error';
        case 'submitted':
          return 'processing';
        case 'under_review':
          return 'processing';
        default:
          return 'default';
      }
    };

    // 状态图标映射
    const getStatusIcon = (statusValue: string) => {
      switch (statusValue) {
        case 'approved':
          return <CheckOutlined />;
        case 'rejected':
          return <CloseOutlined />;
        case 'submitted':
          return <ClockCircleOutlined />;
        case 'under_review':
          return <ClockCircleOutlined />;
        default:
          return <FileTextOutlined />;
      }
    };

    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined style={{ color: '#10b981', fontSize: '20px' }} />
            <span
              style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}
            >
              {t('project:issuerWorkbench.questionnaireStatus.title')}
            </span>
          </div>
        }
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)',
          marginBottom: '20px',
        }}
        bodyStyle={{ padding: '20px' }}
      >
        {/* 状态概览 */}
        <div style={{ marginBottom: '20px' }}>
          <Alert
            message={
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <Tag
                  color={getStatusColor(status.status)}
                  icon={getStatusIcon(status.status)}
                  style={{
                    fontSize: '14px',
                    padding: '4px 12px',
                    fontWeight: '500',
                  }}
                >
                  {status.status === 'approved'
                    ? t('project:issuerWorkbench.questionnaireStatus.approved')
                    : status.status === 'rejected'
                      ? t(
                          'project:issuerWorkbench.questionnaireStatus.rejected'
                        )
                      : status.status === 'submitted'
                        ? t(
                            'project:issuerWorkbench.questionnaireStatus.submitted'
                          )
                        : status.status === 'under_review'
                          ? t(
                              'project:issuerWorkbench.questionnaireStatus.underReview'
                            )
                          : t(
                              'project:issuerWorkbench.questionnaireStatus.draft'
                            )}
                </Tag>
                {status.ai_score && (
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {t('project:issuerWorkbench.questionnaireStatus.aiScore')}:{' '}
                    <strong style={{ color: '#10b981' }}>
                      {status.ai_score}
                      {t('project:issuerWorkbench.questionnaireStatus.points')}
                    </strong>
                  </span>
                )}
                {status.risk_level && (
                  <Tag
                    color={
                      status.risk_level === 'low'
                        ? 'green'
                        : status.risk_level === 'medium'
                          ? 'orange'
                          : 'red'
                    }
                  >
                    {t('screening:detail.fields.riskLevel')}:{' '}
                    {status.risk_level === 'low'
                      ? t('screening:detail.riskLevel.low')
                      : status.risk_level === 'medium'
                        ? t('screening:detail.riskLevel.medium')
                        : t('screening:detail.riskLevel.high')}
                  </Tag>
                )}
              </div>
            }
            description={status.status_description}
            type={
              status.status === 'approved'
                ? 'success'
                : status.status === 'rejected'
                  ? 'error'
                  : 'info'
            }
            showIcon
            style={{ marginBottom: '16px' }}
          />
        </div>

        {/* 审核详情 */}
        {status.reviewed_at && (
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col xs={24} md={12}>
              <div>
                <Text
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    display: 'block',
                  }}
                >
                  {t('project:issuerWorkbench.questionnaireStatus.reviewTime')}
                </Text>
                <Text
                  style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#1f2937',
                  }}
                >
                  {new Date(status.reviewed_at).toLocaleString('zh-CN')}
                </Text>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div>
                <Text
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    display: 'block',
                  }}
                >
                  {t('project:issuerWorkbench.questionnaireStatus.reviewedBy')}
                </Text>
                <Text
                  style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#1f2937',
                  }}
                >
                  {status.reviewed_by || t('common:systemAutoReview')}
                </Text>
              </div>
            </Col>
          </Row>
        )}

        {/* 审核意见 */}
        {status.review_comments && (
          <div style={{ marginBottom: '20px' }}>
            <Text
              style={{
                fontSize: '14px',
                color: '#6b7280',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              {t('project:issuerWorkbench.questionnaireStatus.reviewComments')}
            </Text>
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.05)',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#1f2937',
              }}
            >
              {status.review_comments}
            </div>
          </div>
        )}

        {/* 拒绝原因 */}
        {status.status === 'rejected' &&
          status.rejection_reasons &&
          status.rejection_reasons.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <Text
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                {t('project:issuerWorkbench.questionnaireStatus.reviewReasons')}
              </Text>
              <div
                style={{
                  background: 'rgba(239, 68, 68, 0.05)',
                  border: '1px solid rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                {status.rejection_reasons.map((reason, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom:
                        index < status.rejection_reasons.length - 1
                          ? '8px'
                          : '0',
                    }}
                  >
                    <ExclamationCircleOutlined
                      style={{ color: '#ef4444', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '14px', color: '#1f2937' }}>
                      {reason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* 下一步操作 */}
        {(status.next_actions && status.next_actions.length > 0) ||
        status.status === 'rejected' ? (
          <div>
            <Text
              style={{
                fontSize: '14px',
                color: '#6b7280',
                display: 'block',
                marginBottom: '12px',
              }}
            >
              {t('project:issuerWorkbench.suggestedActions')}
            </Text>
            <Space wrap>
              {/* 如果是拒绝状态，优先显示重新提交按钮 */}
              {status.status === 'rejected' && (
                <>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setResubmissionModalVisible(true)}
                    style={{
                      background:
                        'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '500',
                    }}
                  >
                    {t('project:issuerWorkbench.questionnaireStatus.resubmit')}
                  </Button>
                  <Button
                    icon={<HistoryOutlined />}
                    onClick={() => setResubmissionModalVisible(true)}
                  >
                    {t('project:issuerWorkbench.viewReviewHistory')}
                  </Button>
                </>
              )}

              {/* 原有的下一步操作 - Bug #20: 只显示已实现的功能 */}
              {status.next_actions
                ?.filter(action => {
                  // 只保留已实现的功能
                  if (typeof action === 'string') {
                    return false; // 字符串类型的动作暂时都隐藏
                  }
                  // 只显示已实现的功能（modify_questionnaire）
                  return action.action === 'modify_questionnaire';
                })
                .map((action, index) => {
                  return (
                    <Button
                      key={action.action}
                      type={
                        status.status === 'rejected'
                          ? 'default'
                          : action.priority === 'high'
                            ? 'primary'
                            : 'default'
                      }
                      size="small"
                      onClick={() => {
                        if (action.action === 'modify_questionnaire') {
                          setResubmissionModalVisible(true);
                        }
                      }}
                      title={action.description}
                    >
                      {action.title}
                    </Button>
                  );
                })}
            </Space>
          </div>
        ) : null}
      </Card>
    );
  };

  // 处理加载状态
  if (isLoading) {
    return (
      <StandardPageLayout
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Spin
            size="large"
            tip={t('project:issuerWorkbench.loadingProjectData')}
          />
        </div>
      </StandardPageLayout>
    );
  }

  // 处理错误状态
  if (error) {
    return (
      <StandardPageLayout
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
      >
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('project:issuerWorkbench.loadProjectFailed')}
          >
            <Button type="primary" onClick={() => refetch()}>
              <ReloadOutlined /> {t('project:message.reload')}
            </Button>
          </Empty>
        </div>
      </StandardPageLayout>
    );
  }

  // 如果没有项目数据
  if (!displayProject) {
    return (
      <StandardPageLayout
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
      >
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Empty description={t('project:issuerWorkbench.projectNotFound')}>
            <Button
              type="primary"
              onClick={() => navigate('/admin/project-management')}
            >
              {t('project:issuerWorkbench.backToProjects')}
            </Button>
          </Empty>
        </div>
      </StandardPageLayout>
    );
  }

  return (
    <StandardPageLayout
      title={title}
      subtitle={subtitle}
      breadcrumbs={breadcrumbs}
      statistics={statistics}
      actions={actions}
      customBannerContent={customBannerContent}
    >
      <div style={{ padding: '0 24px 24px' }}>
        <Row gutter={[24, 24]}>
          {/* 左侧主要内容 */}
          <Col xs={24} lg={16}>
            {/* 问卷审核状态 - 优先显示 */}
            {renderQuestionnaireStatus()}

            {/* 简化的项目动态 */}
            <Card
              title={
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <CalendarOutlined
                    style={{ color: '#8b5cf6', fontSize: '20px' }}
                  />
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                    }}
                  >
                    {t('project:issuerWorkbench.projectTimeline')}
                  </span>
                </div>
              }
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)',
                marginBottom: '20px',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Timeline
                items={[
                  {
                    color: 'green',
                    children: (
                      <div>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#1f2937',
                          }}
                        >
                          {t(
                            'project:issuerWorkbench.screeningReviewCompleted'
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            marginTop: '2px',
                          }}
                        >
                          {displayProject.review_status?.reviewed_at
                            ? new Date(
                                displayProject.review_status.reviewed_at
                              ).toLocaleString('zh-CN')
                            : '2024-03-20 14:30'}{' '}
                          •{' '}
                          {displayProject.review_status?.reviewed_by ||
                            t('project:issuerWorkbench.system')}
                        </div>
                      </div>
                    ),
                  },
                  {
                    color: 'blue',
                    children: (
                      <div>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#1f2937',
                          }}
                        >
                          {t('project:issuerWorkbench.questionnaireSubmitted')}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            marginTop: '2px',
                          }}
                        >
                          {displayProject.review_status?.submitted_at
                            ? new Date(
                                displayProject.review_status.submitted_at
                              ).toLocaleString('zh-CN')
                            : '2024-03-19 10:15'}{' '}
                          • {displayProject.project_manager}
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>

          {/* 右侧信息面板 */}
          <Col
            xs={24}
            lg={8}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                height: '100%',
              }}
            >
              {/* 发行人概览 */}
              <Card
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <BankOutlined
                      style={{ color: '#10b981', fontSize: '20px' }}
                    />
                    <span
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      {t('project:issuerWorkbench.issuerOverview')}
                    </span>
                  </div>
                }
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)',
                }}
                bodyStyle={{ padding: '16px' }}
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div>
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t('project:issuerWorkbench.companyName')}
                    </Text>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      {displayProject.issuer_name}
                    </div>
                  </div>
                  <div>
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t('project:issuerWorkbench.mainBusiness')}
                    </Text>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      {displayProject.green_project_type}
                    </div>
                  </div>
                  <div>
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t('project:issuerWorkbench.creditRating')}
                    </Text>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      {displayProject.questionnaire_details?.issuer_rating_scale
                        ?.creditRating || 'AAA'}
                    </div>
                  </div>
                  <div>
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t('project:issuerWorkbench.riskLevel')}
                    </Text>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      <Tag
                        color={
                          displayProject.risk_level === 'low'
                            ? 'green'
                            : displayProject.risk_level === 'medium'
                              ? 'orange'
                              : 'red'
                        }
                      >
                        {displayProject.risk_level === 'low'
                          ? t('project:common.risk.low')
                          : displayProject.risk_level === 'medium'
                            ? t('project:common.risk.medium')
                            : t('project:common.risk.high')}
                      </Tag>
                    </div>
                  </div>
                </Space>
              </Card>

              {/* 项目团队 */}
              <Card
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <TeamOutlined
                      style={{ color: '#6366f1', fontSize: '20px' }}
                    />
                    <span
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1f2937',
                      }}
                    >
                      {t('project:issuerWorkbench.projectTeam.title')}
                    </span>
                  </div>
                }
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)',
                  flex: 1,
                }}
                bodyStyle={{ padding: '16px', height: '100%' }}
              >
                <List
                  dataSource={getProjectTeam(t)}
                  renderItem={member => (
                    <List.Item style={{ padding: '8px 0', border: 'none' }}>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              background: '#10b981',
                              fontSize: '10px',
                              width: '24px',
                              height: '24px',
                              lineHeight: '24px',
                            }}
                          >
                            {member.avatar}
                          </Avatar>
                        }
                        title={
                          <div
                            style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#1f2937',
                              lineHeight: '1.2',
                            }}
                          >
                            {member.name}
                          </div>
                        }
                        description={
                          <div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {member.role}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              {member.phone}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </div>
          </Col>
        </Row>
      </div>

      {/* 重新提交模态框 */}
      {displayProject && (
        <QuestionnaireResubmissionModal
          visible={resubmissionModalVisible}
          onCancel={() => setResubmissionModalVisible(false)}
          questionnaireId={displayProject.id}
          projectName={displayProject.name}
          onResubmitSuccess={() => {
            message.success(t('project:message.resubmitSuccess'));
            setResubmissionModalVisible(false);
            refetch(); // 重新获取项目数据
          }}
        />
      )}
    </StandardPageLayout>
  );
};

export default IssuerWorkbench;
